/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IDistributorSelectorViewProps } from './distributor-selector';

/**
 * Render the body of the popup.
 * @param props -- Distributor selector view props.
 * @returns -- Returns the html.
 */
const renderDistributorSelectorBody: React.FC<IDistributorSelectorViewProps> = props => {
    const {
        bodyContainerProps,
        state,
        spinner,
        distributorLinesViewProps,
        maps,
        bodyWrapperProps,
        distributorsResultContainerProps,
        distributorsResultWrapperProps,
        mapContainerProps,
        distributorCountMessage,
        searchForm,
        noDistributorsMessage
    } = props;

    return (
        <Node {...bodyContainerProps}>
            <Node {...bodyWrapperProps!}>
                {searchForm}
                <Node {...distributorsResultContainerProps}>
                    {state.isSearchInProgress ? (
                        spinner
                    ) : (
                        <Node {...distributorsResultWrapperProps}>
                            {distributorLinesViewProps ? (
                                <>
                                    {distributorCountMessage}
                                    {distributorLinesViewProps}
                                </>
                            ) : (
                                <>{noDistributorsMessage}</>
                            )}
                        </Node>
                    )}
                </Node>
                {mapContainerProps ? (
                    <Node {...mapContainerProps}>
                        {maps}
                        <Node {...distributorsResultWrapperProps}>{distributorLinesViewProps}</Node>
                    </Node>
                ) : (
                    undefined
                )}
            </Node>
        </Node>
    );
};

const renderDistributorDetails: React.FC<IDistributorSelectorViewProps> = props => {
    const {
        distributorDetailsSliderWrapper,
        distributorDetailsSliderHeaderContainer,
        distributorDetailsSliderHeaderContent,
        distributorDetailsSliderBodyContainer,
        distributorDetailsSliderCurrentDistributorTitle,
        distributorDetailsSliderFooterContainer,
        distributorDetailsSliderDistributorName,
        distributorDetailsSliderInformationWrapper,
        distributorDetailsLocation,
        distributorDetailsPrimaryContact,
        distributorDetailsSliderSelectDistributor,
        terms,
        distributorDetailsChangeDistributor,
        state,
        distributorDetailsDeliveryMethods
    } = props;

    return (
        <Node {...distributorDetailsSliderWrapper}>
            {!state.distributorDetailsFromPopup ? (
                <Node {...distributorDetailsSliderHeaderContainer}>{distributorDetailsSliderHeaderContent}</Node>
            ) : null}
            <Node {...distributorDetailsSliderBodyContainer}>
                {distributorDetailsSliderCurrentDistributorTitle}
                {distributorDetailsSliderDistributorName}
                <Node {...distributorDetailsSliderInformationWrapper}>
                    {distributorDetailsLocation}
                    {distributorDetailsPrimaryContact}
                    {distributorDetailsDeliveryMethods}
                </Node>
                {distributorDetailsSliderSelectDistributor}
                {state.distributorDetailsFromPopup ? distributorDetailsChangeDistributor : null}
            </Node>
            <Node {...distributorDetailsSliderFooterContainer}> {terms}</Node>
        </Node>
    );
};

const renderDistributorWarningBody: React.FC<IDistributorSelectorViewProps> = props => {
    const { bodyContainerProps, distributorWarningContent } = props;

    return <Node {...bodyContainerProps}>{distributorWarningContent}</Node>;
};

const renderDistributorSelectorModal: React.FC<IDistributorSelectorViewProps> = props => {
    const { footerContainerProps, headerContainerProps, distributorSelectorHeaderProps, moduleProps, terms } = props;
    return (
        <Module {...moduleProps}>
            {renderDistributorDetails(props)}
            <Node {...headerContainerProps}>{distributorSelectorHeaderProps}</Node>
            {renderDistributorSelectorBody(props)}
            <Node {...footerContainerProps}>{terms}</Node>
        </Module>
    );
};

const renderDistributorDetailsPopup = (props: IDistributorSelectorViewProps): JSX.Element | null => {
    const {
        distributorDetailsModalPopup,
        distributorDetailsModalHeader,
        distributorDetailsHeaderProps,
        distributorDetailsModalBody,
        distributorDetailsModalBodyContainer,
        distributorDetailsLocation,
        distributorDetailsPrimaryContact,
        distributorDetailsViewAllDetails,
        distributorDetailsChangeDistributor,
        distributorDetailsModalFooter,
        showDistributorDetailsPopup,
        distributorDetailsDeliveryMethods
    } = props;
    if (showDistributorDetailsPopup) {
        return (
            <Module {...distributorDetailsModalPopup}>
                <Node {...distributorDetailsModalHeader}>{distributorDetailsHeaderProps}</Node>
                <Node {...distributorDetailsModalBody}>
                    <Node {...distributorDetailsModalBodyContainer}>
                        {distributorDetailsLocation}
                        {distributorDetailsPrimaryContact}
                        {distributorDetailsDeliveryMethods}
                    </Node>
                    {distributorDetailsViewAllDetails}
                </Node>
                <Node {...distributorDetailsModalFooter}>{distributorDetailsChangeDistributor}</Node>
            </Module>
        );
    }
    return null;
};

const renderWarningDialog = (props: IDistributorSelectorViewProps): JSX.Element | undefined => {
    const {
        footerContainerProps,
        headerContainerProps,
        distributorWarningHeaderProps,
        moduleProps,
        distributorWarningButtonWrapper,
        distributorWarningDoNotShowAgain,
        distributorWarningContinue,
        distributorWarningCancel
    } = props;
    return (
        <Module {...moduleProps}>
            <Node {...headerContainerProps}>{distributorWarningHeaderProps}</Node>
            {renderDistributorWarningBody(props)}
            <Node {...footerContainerProps}>
                {distributorWarningDoNotShowAgain}
                <Node {...distributorWarningButtonWrapper}>
                    {distributorWarningCancel} {distributorWarningContinue}
                </Node>
            </Node>
        </Module>
    );
};

/**
 * Distributor selector view props.
 * @param props - Distributor selector view props.
 * @returns - HTML.
 */
const DistributorSelectorView: React.FC<IDistributorSelectorViewProps> = props => {
    const { currentDistributorContainer, currentDistributorButton, state } = props;

    return currentDistributorContainer ? (
        <Module {...currentDistributorContainer}>
            {currentDistributorButton}
            {renderDistributorDetailsPopup(props)}
            {state.showWarningDialog ? renderWarningDialog(props) : renderDistributorSelectorModal(props)}
        </Module>
    ) : null;
};

export default DistributorSelectorView;
